import { Head, usePage } from '@inertiajs/react';
import { useEffect, useRef } from 'react';
import sigilBlue from '../../images/SigilBlue.png';

interface PageProps {
    flash?: {
        error?: string;
        success?: string;
    };
}

/**
 * Star data for the twinkling animation.
 */
interface Star {
    x: number;
    y: number;
    size: number;
    opacity: number;
    twinkleSpeed: number;
    twinkleOffset: number;
}

/**
 * StarryBackground component - renders animated twinkling stars and nebula effects.
 * Uses canvas for performant star rendering with CSS for nebula gradients.
 */
function StarryBackground() {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const starsRef = useRef<Star[]>([]);
    const animationRef = useRef<number>(0);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        // Set canvas size to window size
        const resizeCanvas = () => {
            canvas.width = window.innerWidth;
            canvas.height = window.innerHeight;
            // Regenerate stars on resize
            generateStars();
        };

        // Generate random stars
        const generateStars = () => {
            const starCount = Math.floor((canvas.width * canvas.height) / 3000); // Density based on screen size
            starsRef.current = [];

            for (let i = 0; i < starCount; i++) {
                starsRef.current.push({
                    x: Math.random() * canvas.width,
                    y: Math.random() * canvas.height,
                    size: Math.random() * 2 + 0.5, // 0.5 to 2.5 pixels
                    opacity: Math.random() * 0.5 + 0.3, // 0.3 to 0.8 base opacity
                    twinkleSpeed: Math.random() * 0.003 + 0.001, // Slower twinkling speed
                    twinkleOffset: Math.random() * Math.PI * 2, // Random phase offset
                });
            }
        };

        // Animation loop for twinkling stars
        const animate = (time: number) => {
            ctx.clearRect(0, 0, canvas.width, canvas.height);

            starsRef.current.forEach((star) => {
                // Calculate twinkling opacity using sine wave
                const twinkle = Math.sin(time * star.twinkleSpeed + star.twinkleOffset);
                const currentOpacity = star.opacity + twinkle * 0.3;

                ctx.beginPath();
                ctx.arc(star.x, star.y, star.size, 0, Math.PI * 2);
                ctx.fillStyle = `rgba(255, 255, 255, ${Math.max(0.1, Math.min(1, currentOpacity))})`;
                ctx.fill();

                // Add a subtle glow to larger stars
                if (star.size > 1.5) {
                    ctx.beginPath();
                    ctx.arc(star.x, star.y, star.size * 2, 0, Math.PI * 2);
                    ctx.fillStyle = `rgba(200, 220, 255, ${currentOpacity * 0.2})`;
                    ctx.fill();
                }
            });

            animationRef.current = requestAnimationFrame(animate);
        };

        resizeCanvas();
        window.addEventListener('resize', resizeCanvas);
        animationRef.current = requestAnimationFrame(animate);

        return () => {
            window.removeEventListener('resize', resizeCanvas);
            cancelAnimationFrame(animationRef.current);
        };
    }, []);

    return (
        <div className="fixed inset-0 overflow-hidden pointer-events-none">
            {/* Base dark sky gradient */}
            <div className="absolute inset-0 bg-gradient-to-b from-slate-950 via-slate-900 to-indigo-950" />

            {/* Nebula effects - soft colorful clouds */}
            <div className="absolute inset-0">
                {/* Purple nebula - top right */}
                <div
                    className="absolute top-0 right-0 w-[60%] h-[50%] opacity-30"
                    style={{
                        background: 'radial-gradient(ellipse at 70% 20%, rgba(139, 92, 246, 0.4) 0%, rgba(139, 92, 246, 0.1) 40%, transparent 70%)',
                    }}
                />
                {/* Blue nebula - bottom left */}
                <div
                    className="absolute bottom-0 left-0 w-[50%] h-[40%] opacity-25"
                    style={{
                        background: 'radial-gradient(ellipse at 30% 80%, rgba(59, 130, 246, 0.4) 0%, rgba(59, 130, 246, 0.1) 40%, transparent 70%)',
                    }}
                />
                {/* Pink/magenta nebula - center */}
                <div
                    className="absolute top-1/3 left-1/4 w-[40%] h-[30%] opacity-20"
                    style={{
                        background: 'radial-gradient(ellipse at 50% 50%, rgba(236, 72, 153, 0.3) 0%, rgba(168, 85, 247, 0.1) 50%, transparent 80%)',
                    }}
                />
                {/* Teal accent - bottom right */}
                <div
                    className="absolute bottom-1/4 right-1/4 w-[30%] h-[25%] opacity-15"
                    style={{
                        background: 'radial-gradient(ellipse at 50% 50%, rgba(20, 184, 166, 0.3) 0%, transparent 70%)',
                    }}
                />
            </div>

            {/* Canvas for animated stars */}
            <canvas ref={canvasRef} className="absolute inset-0" />
        </div>
    );
}

/**
 * MoonLogo component - renders a moon with the Sellune sigil overlaid.
 * Uses Libre Baskerville font for the title text.
 * Responsive sizing: smaller on mobile (w-56), medium on tablet (w-72), larger on desktop (w-96).
 */
function MoonLogo() {
    return (
        <div className="relative w-56 h-56 sm:w-72 sm:h-72 lg:w-96 lg:h-96">
            {/* Outer glow effect */}
            <div
                className="absolute inset-0 rounded-full blur-3xl opacity-40"
                style={{
                    background: 'radial-gradient(circle, rgba(200, 210, 255, 0.6) 0%, rgba(139, 92, 246, 0.3) 50%, transparent 70%)',
                }}
            />

            {/* Moon SVG */}
            <svg viewBox="0 0 200 200" className="absolute inset-0 w-full h-full drop-shadow-2xl">
                <defs>
                    {/* Moon surface gradient */}
                    <radialGradient id="moonGradient" cx="40%" cy="40%" r="60%">
                        <stop offset="0%" stopColor="#E8E8F0" />
                        <stop offset="50%" stopColor="#C8C8D8" />
                        <stop offset="100%" stopColor="#A0A0B8" />
                    </radialGradient>

                    {/* Atmospheric glow */}
                    <radialGradient id="atmosphereGlow" cx="50%" cy="50%" r="50%">
                        <stop offset="70%" stopColor="transparent" />
                        <stop offset="90%" stopColor="rgba(180, 190, 255, 0.2)" />
                        <stop offset="100%" stopColor="rgba(140, 150, 220, 0.4)" />
                    </radialGradient>
                </defs>

                {/* Main moon circle */}
                <circle cx="100" cy="100" r="85" fill="url(#moonGradient)" />

                {/* Crater details - subtle dark spots */}
                <circle cx="70" cy="60" r="12" fill="rgba(120, 120, 140, 0.2)" />
                <circle cx="130" cy="80" r="8" fill="rgba(120, 120, 140, 0.15)" />
                <circle cx="90" cy="120" r="15" fill="rgba(120, 120, 140, 0.18)" />
                <circle cx="50" cy="100" r="6" fill="rgba(120, 120, 140, 0.12)" />
                <circle cx="140" cy="130" r="10" fill="rgba(120, 120, 140, 0.14)" />
                <circle cx="110" cy="50" r="5" fill="rgba(120, 120, 140, 0.1)" />

                {/* Atmospheric glow ring */}
                <circle cx="100" cy="100" r="88" fill="none" stroke="url(#atmosphereGlow)" strokeWidth="8" />
            </svg>

            {/* Sigil overlay - centered on moon */}
            <div className="absolute inset-0 flex items-center justify-center">
                <img
                    src={sigilBlue}
                    alt="Sellune Sigil"
                    className="w-[150%] h-[150%] object-cover opacity-90 drop-shadow-lg"
                    style={{
                        filter: 'drop-shadow(0 0 20px rgba(100, 150, 255, 0.5))',
                    }}
                />
            </div>

            {/* Title below the moon - using Libre Baskerville font */}
            <div className="absolute -bottom-2 sm:-bottom-4 left-0 right-0 text-center">
                <h2
                    className="text-2xl sm:text-3xl lg:text-4xl font-normal text-white/90 tracking-[0.2em] sm:tracking-[0.3em] drop-shadow-lg"
                    style={{ fontFamily: "'Libre Baskerville', serif" }}
                >
                    SELLUNE
                </h2>
            </div>
        </div>
    );
}

/**
 * Home page - Landing page with Discord login.
 * Shows a moon logo with the Sellune sigil on the left and login button on the right.
 * Features a starry night sky background with twinkling stars and nebula effects.
 * Uses Libre Baskerville font for the "SELLUNE" title text.
 * Displays flash messages for errors (e.g., Discord server membership denied).
 */
export default function Home() {
    const { flash } = usePage<PageProps>().props;

    return (
        <>
            <Head title="Welcome" />

            {/* Starry background with nebula effects */}
            <StarryBackground />

            <div className="relative min-h-screen flex items-center justify-center p-4 py-8">
                <div className="max-w-6xl w-full grid lg:grid-cols-2 gap-6 lg:gap-8 items-center">
                    {/* Moon logo with sigil - visible on all screen sizes */}
                    <div className="flex items-center justify-center mb-4 lg:mb-0">
                        <MoonLogo />
                    </div>

                    {/* Right side - Login content */}
                    <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-8 lg:p-12 border border-white/20">
                        <div className="text-center lg:text-left">
                            <h1 className="text-4xl lg:text-5xl font-bold text-white mb-4">
                                Welcome to Sellune
                            </h1>
                            <p className="text-lg text-gray-300 mb-8">
                                A collaborative worldbuilding experience. Claim your hex, build your story,
                                and explore a world created together.
                            </p>

                            <div className="space-y-4">
                                {/* Error message for denied access (e.g., not a Discord server member) */}
                                {flash?.error && (
                                    <div className="bg-red-500/20 border border-red-500/50 rounded-lg p-4 text-red-200 text-sm">
                                        {flash.error}
                                    </div>
                                )}

                                {/* Discord Login Button - uses <a> tag for full page redirect (required for OAuth) */}
                                <a
                                    href="/auth/discord/redirect"
                                    className="w-full flex items-center justify-center gap-3 bg-[#5865F2] hover:bg-[#4752C4] text-white font-semibold py-4 px-6 rounded-lg transition-colors"
                                >
                                    {/* Discord Icon */}
                                    <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
                                        <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z" />
                                    </svg>
                                    Continue with Discord
                                </a>

                                <p className="text-sm text-gray-300 text-center">
                                    By continuing, you agree to our{' '}
                                    <a href="/tos" className="text-purple-400 hover:text-purple-300 underline">
                                        terms of service
                                    </a>.
                                </p>
                            </div>
                        </div>

                        {/* Features list */}
                        <div className="mt-12 grid grid-cols-1 sm:grid-cols-3 gap-4">
                            {[
                                { icon: '🗺️', title: 'Claim', desc: 'Your hex awaits' },
                                { icon: '✍️', title: 'Create', desc: 'Build your lore' },
                                { icon: '🌍', title: 'Connect', desc: 'Join the world' },
                            ].map((feature, i) => (
                                <div key={i} className="text-center p-4">
                                    <div className="text-3xl mb-2">{feature.icon}</div>
                                    <h3 className="font-semibold text-white">{feature.title}</h3>
                                    <p className="text-sm text-gray-400">{feature.desc}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}

